// "New" badge for a manually-flagged left-nav item (ENG-17643).
//
// Only ever mounted when the server has already decided the badge should
// show (NavigationHelper#show_nav_new_badge? — isNew AND not yet seen by
// this user), so there is no loading/flash state to manage here: if this
// component exists in the DOM, render the badge.
//
// Non-interactive: the badge itself has no click handler or interactive
// element, so clicking its pixels falls through to the surrounding <a> like
// any other row content. Dismissal ("seen") is instead driven by a listener
// on the nearest ancestor <a> — the actual nav row — so a click ANYWHERE in
// the row (badge included) marks it seen, once, without ever intercepting
// or delaying the navigation it's attached to.
import React, { useEffect, useRef } from "react";

import { Badge } from "bundles/boon-components";
import { userPreferencesApi } from "bundles/projects/api/UserPreferencesApi";

type NavItemBadgeProps = {
  preferenceNamespace: string;
  preferenceKey: string;
};

export const NavItemBadge: React.FC<NavItemBadgeProps> = ({
  preferenceNamespace,
  preferenceKey,
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const dismissedRef = useRef(false);

  useEffect(() => {
    const anchor = anchorRef.current?.closest("a");
    if (!anchor) return undefined;

    const markSeen = () => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;

      // Fire-and-forget PATCH (merge, not set — preserves any other keys in
      // this namespace) that must outlive the page unload the click is about
      // to trigger. keepalive:true is what makes that survive; bypassing
      // userPreferencesApi.merge()'s own fetch (which awaits + parses the
      // response) avoids racing the browser tearing the page down mid-await.
      const req = userPreferencesApi.buildKeepaliveRequest(
        preferenceNamespace,
        { [preferenceKey]: true },
        "PATCH"
      );
      if (!req) return;
      fetch(req.url, {
        method: req.method ?? "PATCH",
        credentials: "same-origin",
        keepalive: true,
        headers: req.headers,
        body: req.body,
      }).catch(() => {
        // Best-effort: worst case the badge reappears next load.
      });
    };

    // Not preventDefault/stopPropagation — the anchor's default navigation is
    // untouched, this only piggy-backs an extra side effect on the same click.
    anchor.addEventListener("click", markSeen);
    return () => anchor.removeEventListener("click", markSeen);
  }, [preferenceNamespace, preferenceKey]);

  return (
    <span ref={anchorRef} className="inline-flex" aria-label="New">
      <Badge message="New" variant="everpine" />
    </span>
  );
};
